import { Instance } from "mobx-state-tree";
import React from "react";
import { SurveyRuleModel } from "../../models/SurveyRuleModel";
import { Stack } from "@mui/material";
import { SurveyRuleConditionForm } from "../forms/SurveyRuleConditionForm";
import {
  Button,
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import { SurveyRuleCardLayout } from "../../layouts/SurveyRuleCardLayout";
import { observer } from "mobx-react";
import { SurveyRuleRowActionButtons } from "../buttons/SurveyRuleRowActionButtons";
import { SurveyRuleActionForm } from "../forms/SurveyRuleActionForm";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSurveyRuleStore } from "../../stores/Hooks";
import { SurveyRuleConditionActionButtons } from "../buttons/SurveyRuleConditionActionButtons";

interface GroupSurveyRuleCardProps {
  surveyRule: Instance<typeof SurveyRuleModel>;
}

export const GroupSurveyRuleCard: React.FC<GroupSurveyRuleCardProps> = observer(
  ({ surveyRule }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const tokens = useFoundationColorTokens();
    const cornerRadius = useCornerRadius();

    const surveyRuleStore = useSurveyRuleStore();

    return (
      <SurveyRuleCardLayout surveyRuleId={surveyRule.ruleId}>
        <Stack
          padding={spacing.spaceMD}
          gap={spacing.spaceMD}
          bgcolor={tokens.backgroundSubtle}
          divider={<DividerComponent width="100%" orientation="horizontal" />}
          borderRadius={cornerRadius.radiusXS}
          width="100%"
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              {surveyRule.conditionOrder.map((conditionId) => (
                <Stack key={conditionId} direction="row" gap={spacing.spaceXS}>
                  <SurveyRuleConditionForm
                    surveyRuleId={surveyRule.ruleId}
                    surveyRuleCondition={surveyRule.getNonNullCondition(
                      conditionId,
                    )}
                  />
                  {surveyRule.conditionOrder.length > 2 && (
                    <SurveyRuleConditionActionButtons
                      rule={surveyRule}
                      conditionId={conditionId}
                    />
                  )}
                </Stack>
              ))}
              <Stack marginTop={spacing.spaceXS}>
                <Button
                  label={t("surveys.rules.addRule")}
                  icon={<Plus />}
                  size="medium"
                  name="addCondition"
                  onClick={() =>
                    surveyRuleStore.addNewCondition(surveyRule.ruleId)
                  }
                  variant="outlined-neutral"
                />
              </Stack>
            </Stack>
            <SurveyRuleRowActionButtons ruleId={surveyRule.ruleId} />
          </Stack>
          <SurveyRuleActionForm
            surveyRuleId={surveyRule.ruleId}
            surveyAction={surveyRule.action}
            surveyTarget={surveyRule.target}
          />
        </Stack>
      </SurveyRuleCardLayout>
    );
  },
);
